import React from 'react';
import PropTypes from 'prop-types';
import { Text, View } from 'react-native';

import Image from '../Image';

import TrackableOnPress from './TrackableOnPress';
import analytics from '../../../modules/analytics';

import './TrackedLink.module.scss';
import { SecretTouchable } from '../developer/SecretTouchable';
import { openExternalUrl } from '../../../modules/openExternalUrl';

/**
 * Displays a text-only link for the sidebar which tracks clicks
 */
const TrackedLink = ({
  onPress,
  onSecretClick,
  children,
  trackingKey,
  className,
  image,
  imageStyle,
  href,
  target,
  style,
  ...linkProps
}) => {
  const handlePress = e => {
    if (href) {
      openExternalUrl(href, target);
    }

    if (typeof onPress === 'function') {
      onPress(e);
    }
  };

  let handleSecretClick;

  if (typeof onSecretClick === 'function') {
    handleSecretClick = () => {
      analytics.track('sidebar_secret_click', { item: trackingKey });
      onSecretClick();
    };
  }

  const allLinkProps = {
    ...linkProps,
    href,
  };

  return (
    <TrackableOnPress
      trackingKey={trackingKey}
      onPress={handlePress}
      className={className}
      style={style}
    >
      {image ? (
        <Text accessibilityRole="link" {...allLinkProps}>
          <Image source={image} style={imageStyle} {...allLinkProps} />
        </Text>
      ) : (
        <Text styleName="link-text" accessibilityRole="link" {...allLinkProps}>{children}</Text>
      )}
      {typeof handleSecretClick === 'function' ? (
        <SecretTouchable
          onSecretClick={handleSecretClick}
          onClick={() => {}}
        >
          <View style={{
            minWidth: 50,
            height: '100%',
            minHeight: 40,
          }}
          />
        </SecretTouchable>
      ) : null}
    </TrackableOnPress>
  );
};

TrackedLink.propTypes = {
  /** ID to use for tracking calls */
  trackingKey: PropTypes.string.isRequired,
  /** Called when the link is clicked */
  onPress: PropTypes.func,
  /** Called when a secret click is detected */
  onSecretClick: PropTypes.func,
  /** component(s) to render within the link */
  children: PropTypes.node,
  /** Class name for the component */
  className: PropTypes.string,
  /** An image to place in the `<Image>` child */
  image: PropTypes.any,
  /** Style to attach to the image */
  imageStyle: PropTypes.object,
  /** URL to link to */
  href: PropTypes.string,
  /** Which window to open */
  target: PropTypes.oneOf([
    '_blank',
    '_system',
  ]),
  /** Specifies the style for the component */
  style: PropTypes.any,
};

TrackedLink.defaultProps = {
  onPress: null,
  onSecretClick: null,
  children: null,
  className: '',
  image: null,
  imageStyle: null,
  href: null,
  target: '_blank',
  style: undefined,
};

export default TrackedLink;
